<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./About.translations.json";
	import thumbnail from "../assets/images/about/Spolecna.jpg";
	import type { AboutProps } from "./AboutProps.js";
	import { setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { contactPathMap } from "../../../core/schema/paths/contactPathMap";
	import Link from "../components/Link.svelte";
	import Icon from "../components/Icon.svelte";
	import { getTenant } from "../getTenant.js";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import bird from "../../../core/assets/icons/bird.svg?raw";
	import zeroOneNumbers from "../../../core/assets/icons/zero-one-numbers.svg?raw";
	import eye from "../../../core/assets/icons/eye.svg?raw";
	import magnifyingGlass from "../../../core/assets/icons/magnifying-glass.svg?raw";
	import grid from "../../../core/assets/icons/grid.svg?raw";
	import layout from "../../../core/assets/icons/layout.svg?raw";
	import implementation from "../../../core/assets/icons/implementation.svg?raw";
	import graph from "../../../core/assets/icons/graph.svg?raw";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import ContactForm from "../components/Contact/ContactForm.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import spiralImg from "../assets/images/about/spiral.jpg";
	import intelligentSoftware1Img from "../assets/images/about/intelligent-software1.png?webp&h=340&w=555&imagetools";
	import intelligentSoftware2Img from "../assets/images/about/intelligent-software2.png?webp&h=340&w=555&imagetools";
	import intelligentSoftware3Img from "../assets/images/about/intelligent-software3.png?webp&h=340&w=555&imagetools";
	import BlogSection from "../components/BlogSection.svelte";
	import { newsToCard } from "../components/newsToCard.js";
	import { careersPathMap } from "../../../core/schema/paths/careersPathMap";
	import { blogPathMap } from "../../../core/schema/paths/blogPathMap.js";
	import HeadingSection from "../components/HeadingSection.svelte";
	import ComputerSection from "../components/ComputerSection.svelte";

	type $$Props = AboutProps;

	const { news } = $$restProps as $$Props;

	const translate = getTranslate(translations);
	const tenant = getTenant();

	setContext(thumbnailImageContextKey, thumbnail);

	const differentiators = [
		{ icon: bird, titleKey: "differentiatorTitle1", descKey: "differentiatorDescription1" },
		{ icon: zeroOneNumbers, titleKey: "differentiatorTitle2", descKey: "differentiatorDescription2" },
		{ icon: eye, titleKey: "differentiatorTitle3", descKey: "differentiatorDescription3" },
	] as const;

	const steps = [
		{ icon: magnifyingGlass, titleKey: "stepTitle1" },
		{ icon: grid, titleKey: "stepTitle2" },
		{ icon: layout, titleKey: "stepTitle3" },
		{ icon: implementation, titleKey: "stepTitle4" },
		{ icon: graph, titleKey: "stepTitle5" },
	] as const;

	const intelligentSoftwareCards = [
		{
			image: intelligentSoftware1Img,
			titleKey: "intelligentSoftwareCardTitle1",
			altKey: "intelligentSoftwareCardAlt1",
			descKey: "intelligentSoftwareCardDesc1",
			linkKey: "intelligentSoftwareCardLink1",
			href: `/${contactPathMap[tenant.locale]}`,
		},
		{
			image: intelligentSoftware2Img,
			titleKey: "intelligentSoftwareCardTitle2",
			altKey: "intelligentSoftwareCardAlt2",
			descKey: "intelligentSoftwareCardDesc2",
		},
		{
			image: intelligentSoftware3Img,
			titleKey: "intelligentSoftwareCardTitle3",
			altKey: "intelligentSoftwareCardAlt3",
			descKey: "intelligentSoftwareCardDesc3",
			linkKey: "intelligentSoftwareCardLink3",
			href: `/${careersPathMap[tenant.locale]}`,
		},
	] as const;
</script>

<Layout>
	<div class="container">
		<HeadingSection tag={translate("tag")} headline={translate("introductoryHeadline")}>
			<div class="lg:text-md/normal mb-5 tracking-[0.02em]">{translate("introductoryDescription1")}</div>
			<div class="lg:text-md/normal mb-5 tracking-[0.02em]">{translate("introductoryDescription2")}</div>
			<Link
				asButton
				variant="secondary"
				class="mt-12 xl:mt-16"
				icon={arrowRight}
				href="/{contactPathMap[tenant.locale]}">{translate("contactUs")}</Link
			>
		</HeadingSection>
	</div>

	<!-- Differentiators -->
	<section class="!mt-0">
		<div class="border-lttertiary border-t">
			<div class="mx-auto max-w-[120rem]">
				<h2 class="text-ltprimary py-10 text-center font-light uppercase tracking-[0.02em]">
					{nbspify(translate("differentiatorsHeadline"))}
				</h2>
			</div>
		</div>

		<div class="border-lttertiary border-y">
			<div class="mx-auto max-w-[120rem]">
				<div class="bg-lttertiary-200 mx-4 grid grid-cols-1 md:mx-12 lg:min-h-[23.938rem] lg:grid-cols-3 xl:mx-32">
					{#each differentiators as item, index (index)}
						<div
							class="border-lttertiary group relative overflow-hidden border-x p-4 md:p-6 lg:border-l-0 lg:border-r lg:p-8"
							class:lg:!border-l={index === 0}
							class:border-b={index !== differentiators.length - 1}
							class:lg:border-b-0={true}
						>
							<div
								class="bg-lttertiary absolute inset-0 translate-y-full transition-transform duration-500 ease-in-out group-hover:translate-y-0"
							></div>
							<div class="relative z-10">
								<Icon icon={item.icon} class="text-ltprimary size-14 lg:size-20" />
								<h3 class="text-ltprimary text-2md mb-0 mt-4 font-normal uppercase tracking-[0.02em] md:mt-6 lg:mt-16">
									{nbspify(translate(item.titleKey))}
								</h3>
								<p class="text-ltsecondary mb-0 mt-4 text-base/[1.6] font-normal">
									{nbspify(translate(item.descKey))}
								</p>
							</div>
						</div>
					{/each}
				</div>
			</div>
		</div>
	</section>

	<!-- Pillars Of Our Work -->
	<section>
		<div class="mx-auto max-w-[120rem]">
			<div class="mx-4 flex flex-col md:flex-row lg:mx-12 lg:gap-4 xl:mx-32 xl:gap-32">
				<div
					class="relative order-2 -mx-4 mt-0 aspect-[3/2] w-[calc(100%+2rem)] flex-1 overflow-hidden rounded-lg md:order-1 md:-mx-0 md:size-[19.875rem] md:self-center lg:size-[24.875rem] xl:h-[47.5rem] xl:w-[48rem]"
				>
					<StaticPicture
						image={spiralImg}
						alt={translate("pillarsOfOurWorkAltText")}
						height={760}
						width={768}
						loading="eager"
						class="absolute inset-0"
						imgClass="absolute inset-0 size-full object-contain md:object-left"
					/>
				</div>

				<div class="text-ltsecondary order-1 flex flex-1 flex-col justify-center md:order-2 xl:justify-center">
					<div class="flex items-center gap-4 pb-12 lg:pb-16">
						<span class="bg-ltprimary-200 h-px w-8"></span>
						<span class="text-ltprimary-200 uppercase">{translate("pillarsOfOurWorkTag")}</span>
					</div>
					<h2 class="text-ltprimary m-0 mb-8 p-0 uppercase tracking-wider">
						{translate("pillarsOfOurWorkHeadline")}
					</h2>
					<div class="lg:text-md/normal mb-5 tracking-[0.02em]">
						{translate("pillarsOfOurWorkParagraph1")}
					</div>
					<div class="lg:text-md/normal mb-5 tracking-[0.02em]">
						{translate("pillarsOfOurWorkParagraph2")}
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Steps -->
	<section>
		<div class="border-lttertiary border-y">
			<div class="mx-auto max-w-[120rem]">
				<div class="border-lttertiary mx-4 border-x md:mx-12 xl:mx-32">
					<div class="grid grid-cols-1 lg:grid-cols-2">
						<div class="mb-6 ml-4 mt-8 flex min-w-0 items-center md:mb-0 md:ml-8 md:mt-12 xl:my-16">
							<h2 class="text-ltprimary m-0 max-w-[39.813rem] uppercase tracking-[0.02em]">
								{nbspify(translate("stepsHeadline"))}
							</h2>
						</div>
						<ol
							class="border-lttertiary grid min-w-0 list-none auto-rows-fr border-t [counter-reset:step] lg:border-l lg:border-t-0"
						>
							{#each steps as step, index (index)}
								<li
									class="bg-lttertiary-100 border-lttertiary before:border-ltprimary-300 before:text-ltprimary-200 grid grid-cols-[auto_1fr_auto] items-center gap-4 px-4 py-5 before:flex before:size-8 before:items-center before:justify-center before:rounded-full before:border before:text-base before:tabular-nums before:tracking-[0.02em] before:content-[counter(step,decimal-leading-zero)] before:[counter-increment:step] md:p-8 md:before:size-10"
									class:border-b={index !== steps.length - 1}
								>
									<span class="text-ltsecondary lg:text-md/normal tracking-[0.02em]">
										{nbspify(translate(step.titleKey))}
									</span>
									<Icon icon={step.icon} class="text-ltprimary-300 ml-1 size-8 shrink-0 md:ml-4 md:size-12" />
								</li>
							{/each}
						</ol>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Intelligent software -->
	<section class="mb-0">
		<div class="mx-auto max-w-[120rem]">
			<ComputerSection>
				<div class="flex items-center gap-4 pb-12 lg:pb-16">
					<span class="bg-ltprimary-200 h-px w-8"></span>
					<span class="text-ltprimary-200 uppercase">{translate("intelligentSoftwareTag")}</span>
				</div>
				<h2 class="text-ltprimary m-0 mb-8 p-0 uppercase tracking-[0.02em]">
					{translate("intelligentSoftwareHeadline")}
				</h2>
				<div class="lg:text-md/normal mb-5 tracking-[0.02em]">
					{translate("intelligentSoftwareParagraph1")}
				</div>
				<div class="lg:text-md/normal mb-5 tracking-[0.02em]">
					{translate("intelligentSoftwareParagraph2")}
				</div>
			</ComputerSection>
		</div>

		<div class="border-lttertiary mt-12 border-y xl:mt-16">
			<div class="mx-auto max-w-[120rem]">
				<div class="border-lttertiary mx-4 grid grid-cols-1 border-x lg:mx-12 xl:mx-32 xl:grid-cols-3 xl:border-r-0">
					{#each intelligentSoftwareCards as card, index (index)}
						<div
							class="border-lttertiary flex min-h-[41.625rem] flex-col xl:border-r"
							class:border-b={index !== intelligentSoftwareCards.length - 1}
							class:xl:border-b-0={true}
						>
							<div class="relative h-[21.25rem] w-full overflow-hidden">
								<StaticPicture
									image={card.image}
									alt={translate(card.altKey)}
									width={555}
									height={340}
									loading="lazy"
									class="absolute inset-0"
									imgClass="absolute inset-0 size-full object-cover"
								/>
								<div class="bg-lttertiary/40 absolute inset-0"></div>
							</div>
							<div class="border-lttertiary bg-lttertiary/40 flex flex-1 flex-col border-t p-4 md:p-8">
								<h3 class="text-ltprimary text-2md mb-4 font-normal uppercase tracking-[0.02em]">
									{nbspify(translate(card.titleKey))}
								</h3>
								<p class="text-ltsecondary mb-6 flex-1 text-sm/[1.6] font-normal tracking-[0.02em] lg:text-base/[1.6]">
									{nbspify(translate(card.descKey))}
								</p>
								{#if "linkKey" in card}
									<Link
										iconGap="mr-0"
										variant="underline"
										class="text-ltprimary-200 font-roboto relative mt-auto text-sm font-medium uppercase tracking-wider"
										icon={arrowRight}
										href={card.href}
									>
										{translate(card.linkKey)}
									</Link>
								{/if}
							</div>
						</div>
					{/each}
				</div>
			</div>
		</div>
		<div class="border-lttertiary mx-auto border-b py-8 md:py-10">
			<Link asButton variant="secondary" class="mx-auto" icon={arrowRight} href="/{contactPathMap[tenant.locale]}"
				>{translate("intelligentSoftwareContactUs")}</Link
			>
		</div>
	</section>

	<!-- Article -->
	<section>
		<h2 class="text-ltprimary my-10 text-center uppercase tracking-[0.02em]">
			{translate("articleHeadline")}
		</h2>
		<BlogSection cards={news.map(newsToCard)} />
		<Link
			asButton
			variant="secondary"
			class="mx-auto my-8 md:my-10"
			icon={arrowRight}
			href="/{blogPathMap[tenant.locale]}">{translate("articleLink")}</Link
		>
	</section>

	<!-- Career -->
	<section>
		<div class="mx-auto max-w-[120rem]">
			<div class="mx-4 flex flex-col md:mx-16 md:flex-row lg:mx-12 lg:gap-4 xl:mx-32 xl:gap-32">
				<div
					class="text-ltsecondary order-2 flex max-w-3xl flex-1 flex-col justify-center md:order-1 xl:justify-center"
				>
					<div class="flex items-center gap-4 pb-12 lg:pb-16">
						<span class="bg-ltprimary-200 h-px w-8"></span>
						<span class="text-ltprimary-200 uppercase">{translate("careerTag")}</span>
					</div>
					<div>
						<h2 class="text-ltprimary m-0 mb-8 p-0 uppercase tracking-wider">
							{translate("careerHeadline")}
						</h2>
						<div class="lg:text-md/normal mb-5 tracking-[0.02em]">
							{translate("careerParagraph1")}
						</div>
						<Link
							asButton
							variant="secondary"
							class="mt-10 md:mt-8 lg:mt-16"
							icon={arrowRight}
							href="/{careersPathMap[tenant.locale]}">{translate("careerLink")}</Link
						>
					</div>
				</div>

				<div
					class="relative order-2 -mx-4 mt-0 aspect-[3/2] w-[calc(100%+2rem)] flex-1 overflow-hidden rounded-lg md:order-2 md:-mx-0 md:size-[19.875rem] md:self-center lg:size-[24.875rem] xl:h-[47.5rem] xl:w-[48rem]"
				>
					<StaticPicture
						image={spiralImg}
						alt={translate("careerAltText")}
						height={760}
						width={768}
						loading="eager"
						class="absolute inset-0"
						imgClass="absolute inset-0 size-full object-contain md:object-left"
					/>
				</div>
			</div>
		</div>
	</section>

	<section>
		<ContactForm />
	</section>
</Layout>
